<template>
  <div
    class="dice-off"
    :class="{ compact: menusCompact, skippable: phase === 'done' }"
    @pointerup="onSkip"
  >
    <button type="button" class="back" @pointerup.stop @click.stop="emit('cancel')">
      {{ t('duel.back') }}
    </button>
    <header v-if="phase !== 'done'">
      <p class="kicker">{{ t('duel.dice.kicker') }}</p>
      <h1>{{ t('duel.dice.title') }}</h1>
      <p class="status">{{ statusText }}</p>
    </header>

    <div class="stage">
      <div class="busts">
        <AvatarView
          :config="leftAvatar"
          crop="bust"
          circle
          :size="bustSize"
        />
        <AvatarView
          :config="rightAvatar"
          crop="bust"
          circle
          :size="bustSize"
        />
      </div>
      <div ref="host" class="canvas-host" />
      <div class="labels">
        <span>{{ leftLabel }}</span>
        <span>{{ rightLabel }}</span>
      </div>
      <div v-if="shown" class="scores">
        <strong>{{ shown[0] }}</strong>
        <span>—</span>
        <strong>{{ shown[1] }}</strong>
      </div>
      <div v-if="phase === 'done'" class="verdict">
        <p class="verdict-kicker">{{ t('duel.dice.kicker') }}</p>
        <p class="verdict-title">{{ verdictText }}</p>
      </div>
    </div>
    <p v-if="phase === 'done'" class="skip-hint">{{ t('duel.vs.skip') }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  DEFAULT_AVATAR,
  makeSeed,
  resolveDiceOff,
  DICE_TIE_MS,
  DICE_RESULT_MS,
  type AvatarConfig,
  type DiceOffResult,
  type D6Value,
  type Seat,
} from '@shared/index'
import { D10Table } from '../game/dice/D10Table'
import { DuelSfx } from '../game/audio/DuelSfx'
import { useCompactLayout } from '../composables/useCompactLayout'
import { useI18n } from '../i18n/t'
import AvatarView from './AvatarView.vue'

const props = withDefaults(
  defineProps<{
    leftLabel: string
    rightLabel: string
    leftAvatar?: AvatarConfig
    rightAvatar?: AvatarConfig
    /** When set, the overlay plays this result (network). Otherwise it rolls locally. */
    preset?: DiceOffResult | null
    /** Seat looking at the table; left die is always "you". */
    viewerSeat?: Seat
  }>(),
  { preset: null, viewerSeat: 0, leftAvatar: () => ({ ...DEFAULT_AVATAR }), rightAvatar: () => ({ ...DEFAULT_AVATAR }) },
)

const emit = defineEmits<{
  done: [result: DiceOffResult]
  cancel: []
}>()

const { t } = useI18n()
const { menusCompact } = useCompactLayout()
const bustSize = computed(() => (menusCompact.value ? 48 : 72))
const host = ref<HTMLElement | null>(null)
let table: D10Table | null = null
const shown = ref<[D6Value, D6Value] | null>(null)
const phase = ref<'idle' | 'rolling' | 'tie' | 'done'>('idle')
const winnerIsViewer = ref(false)

let rollStarted = false
let skipWait: (() => void) | null = null
let closed = false

const statusText = computed(() => {
  if (phase.value === 'tie') return t('duel.dice.tie')
  return t('duel.dice.rolling')
})

const verdictText = computed(() => {
  if (winnerIsViewer.value) return t('duel.dice.youFirstBig')
  return t('duel.dice.namedFirst', { name: props.rightLabel })
})

onMounted(() => {
  if (host.value) table = new D10Table(host.value)
  requestAnimationFrame(() => {
    if (props.preset) {
      void playResult(props.preset)
      return
    }
    void beginLocal()
  })
})

onUnmounted(() => {
  closed = true
  skipWait?.()
  table?.dispose()
  table = null
})

function onSkip(event: PointerEvent): void {
  if (event.button !== 0) return
  if (phase.value !== 'done') return
  skipWait?.()
}

async function beginLocal(): Promise<void> {
  if (rollStarted || props.preset) return
  rollStarted = true
  phase.value = 'rolling'
  const { result } = resolveDiceOff(makeSeed())
  await playResult(result)
}

function facing(round: [D6Value, D6Value]): [D6Value, D6Value] {
  return props.viewerSeat === 1 ? [round[1], round[0]] : round
}

async function playResult(result: DiceOffResult): Promise<void> {
  winnerIsViewer.value = result.firstPlayer === props.viewerSeat
  for (let i = 0; i < result.rounds.length; i++) {
    const pair = result.rounds[i]
    if (!pair) continue
    const round = facing(pair)
    phase.value = i > 0 ? 'tie' : 'rolling'
    shown.value = null
    DuelSfx.play('dice')
    await table?.rollTo(round[0], round[1])
    shown.value = round
    const last = i === result.rounds.length - 1
    if (!last) {
      phase.value = 'tie'
      await wait(DICE_TIE_MS)
    }
  }
  phase.value = 'done'
  DuelSfx.play('turn')
  await wait(DICE_RESULT_MS)
  if (!closed) emit('done', result)
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms)
    function finish(): void {
      if (skipWait === finish) skipWait = null
      clearTimeout(timer)
      resolve()
    }
    skipWait = finish
  })
}
</script>

<style scoped>
.dice-off {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 16px 32px;
  overflow: hidden;
  background: rgba(7, 8, 14, 0.92);
  color: #e5e1e6;
  font-family: Roboto, system-ui, sans-serif;
}

header {
  text-align: center;
}

.kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary, #e8c547);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 8px;
  font-size: 32px;
  font-weight: 400;
}

.status {
  margin: 0;
  min-height: 24px;
  color: #c6c5d0;
  font-size: 16px;
}

.stage {
  position: relative;
  width: min(780px, 100%);
  min-width: 0;
  height: min(440px, 54vh);
}

.canvas-host {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.skippable {
  cursor: pointer;
}

.busts {
  position: absolute;
  left: 8%;
  right: 8%;
  top: 52px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}

.labels,
.scores {
  position: absolute;
  left: 8%;
  right: 8%;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  font-weight: 500;
  letter-spacing: 0.04em;
}

.labels {
  bottom: 10px;
  z-index: 3;
  color: #eceaf0;
  font-size: clamp(18px, 2.6vw, 24px);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.75);
}

.labels span {
  max-width: 46%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scores {
  top: 8px;
  justify-content: center;
  gap: 20px;
  color: var(--md-sys-color-primary, #e8c547);
  font-size: clamp(36px, 6vw, 56px);
  font-weight: 700;
  line-height: 1;
  z-index: 2;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.7);
}

.verdict {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  padding: 20px 16px 24px;
  text-align: center;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(7, 8, 14, 0.82) 28%,
    rgba(7, 8, 14, 0.82) 72%,
    transparent
  );
  animation: verdict-in 0.14s ease-out;
}

.verdict-kicker {
  margin: 0 0 6px;
  color: #c6c5d0;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.verdict-title {
  margin: 0;
  color: var(--md-sys-color-primary, #e8c547);
  font-size: clamp(40px, 8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  text-shadow: 0 6px 28px rgba(0, 0, 0, 0.65);
}

@keyframes verdict-in {
  from {
    opacity: 0;
    transform: translateY(-40%) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.back {
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 8px 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #c6c5d0;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.back:hover {
  color: #fff;
}

.skip-hint {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #c6c5d0;
  opacity: 0.75;
}

/* Short landscape phones and narrow screens: the tray's front lip
   sits on the name row. Keep that band clear of the WebGL image. */
.compact {
  justify-content: flex-start;
  gap: 6px;
  padding: 46px 12px 12px;
}

.compact h1 {
  margin-bottom: 2px;
  font-size: 22px;
}

.compact header {
  flex-shrink: 0;
}

.compact .status {
  min-height: 20px;
  font-size: 14px;
}

.compact .stage {
  flex: 1 1 auto;
  width: min(780px, 100%);
  height: auto;
  min-height: 168px;
  max-height: 300px;
}

.compact .canvas-host {
  bottom: 42px;
}

.compact .busts {
  top: 0;
}

.compact .labels {
  bottom: 0;
  align-items: flex-end;
  padding-bottom: 2px;
  font-size: 15px;
}
</style>
